import i18next from 'i18next';
import capitalize from 'lodash.capitalize';
import { atom, selector, selectorFamily } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { getConfig } from '@/config/config';
import { DEFAULT_ACCENT_COLOR, DEFAULT_BACKGROUND_COLOR, languageByCountryCode, } from '@/config/constants';
import { storage } from '@/util/safe-storage';
import { isMobileSelector, overviewSelector } from './session';
// Default configuration for the web client
const widgetDefaultConfig = Object.freeze({
    accent_color: DEFAULT_ACCENT_COLOR,
    auto_open: false,
    avatar: null,
    background_color: DEFAULT_BACKGROUND_COLOR,
    bubble_image: null,
    chatWindow: {
        height: '650px',
        width: '380px',
    },
    close_session_if_empty: false,
    download_conversation: true,
    file_upload: false,
    focus_trap: false,
    font_family: null,
    header_title: 'common.ai_agent',
    launcher: {
        icon_url: null,
        padding: null,
        show: true,
        text: null,
        type: 'bubble',
    },
    manual_link_handling: false,
    page_triggers: null,
    rate_responses: true,
    reverse_header_buttons: false,
    show_powered_by: true,
    show_reset: false,
    show_survey: true,
    sticky_session: false,
    theme: 'light',
    virtual_agent_name: '',
    visitor_information: {
        disclaimer: null,
        fields: null,
        headline: null,
    },
    waiting_time_threshold: 0,
    webchat_aria_label: '',
    welcome_message: null,
    welcome_screen: {
        enabled: false,
        greeting: null,
        starters: null,
    },
    welcome_trigger_message: null,
    widget_alignment: 'right',
    widget_position: 'livechat',
    window_size: 'regular',
});
// Default configuration for the user config
const iframeDefaultConfig = Object.freeze({
    integrationId: '',
    widgetId: '',
    clientUrl: '',
    isMobile: false,
});
export const userConfigState = atom({
    key: 'userConfigState',
    default: Object.assign(Object.assign({}, widgetDefaultConfig), iframeDefaultConfig), // Add `& WidgetConfig` to set default values
});
export const initialUserConfig = atom({
    key: 'initialUserConfig',
    default: {},
});
export const initialLanguageSelector = selector({
    key: 'initialLanguageSelector',
    get: ({ get }) => {
        const userConfig = get(initialUserConfig);
        return userConfig === null || userConfig === void 0 ? void 0 : userConfig.language;
    },
});
export const integrationIdSelector = selector({
    key: 'integrationIdSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return userConfig === null || userConfig === void 0 ? void 0 : userConfig.integrationId;
    },
});
export const hostSelector = selector({
    key: 'hostSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return (userConfig === null || userConfig === void 0 ? void 0 : userConfig.host) || getConfig().CHANNELS_HOST;
    },
});
export const versionSelector = selector({
    key: 'versionSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return (userConfig === null || userConfig === void 0 ? void 0 : userConfig.version) || 'latest';
    },
});
export const variantSelector = selector({
    key: 'variantSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return {
            isNbg: (userConfig === null || userConfig === void 0 ? void 0 : userConfig.variant) === 'nbg',
        };
    },
});
export const identityTokenSelector = selector({
    key: 'identityTokenSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return userConfig === null || userConfig === void 0 ? void 0 : userConfig.identityToken;
    },
});
export const widgetIdSelector = selector({
    key: 'widgetIdSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return userConfig === null || userConfig === void 0 ? void 0 : userConfig.widgetId;
    },
});
export const isStickySessionSelector = selector({
    key: 'isStickySessionSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        return storage !== null && Boolean(userConfig === null || userConfig === void 0 ? void 0 : userConfig.sticky_session);
    },
});
export const backgroundColorSelector = selector({
    key: 'backgroundColorSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.background_color;
    },
});
export const downloadConversationSelector = selector({
    key: 'downloadConversationSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.download_conversation;
    },
});
export const headerSelector = selector({
    key: 'headerTitleSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        const overview = get(overviewSelector);
        if (overview === null || overview === void 0 ? void 0 : overview.agents[0]) {
            return {
                title: overview.agents[0].name,
                status: capitalize(overview.agents[0].status),
            };
        }
        return { title: config.header_title };
    },
});
export const accentColorSelector = selector({
    key: 'accentColorSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.accent_color;
    },
});
export const windowSizeSelector = selector({
    key: 'windowSizeSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.window_size;
    },
});
export const showPoweredBySelector = selector({
    key: 'showPoweredBySelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.show_powered_by;
    },
});
export const webchatAriaLabelSelector = selector({
    key: 'webchatAriaLabelSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.webchat_aria_label;
    },
});
export const waitingTimeThresholdSelector = selector({
    key: 'waitingTimeThresholdSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.waiting_time_threshold;
    },
});
export const rateResponsesSelector = selector({
    key: 'rateResponsesSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.rate_responses;
    },
});
export const welcomeMessageSelector = selector({
    key: 'welcomeMessageSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        // Check if there is a page trigger with a welcome message
        const pageTriggers = config.page_triggers;
        if (pageTriggers) {
            for (const pageTrigger in pageTriggers) {
                const triggerRegExp = new RegExp(pageTrigger);
                const message = pageTriggers[pageTrigger].opening_message;
                if (triggerRegExp.test(config.clientUrl) && message) {
                    return message;
                }
            }
        }
        // Check if there is default welcome message
        if (config.welcome_message) {
            return config.welcome_message;
        }
        return null;
    },
});
export const clientContextSelector = selector({
    key: 'clientContextSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        if (!userConfig.clientUrl) {
            return {};
        }
        const url = new URL(userConfig.clientUrl);
        const params = new URLSearchParams(url.search);
        return {
            client_url: userConfig.clientUrl,
            ref: params.get('ref') || undefined,
        };
    },
});
export const widgetPositionSelector = selector({
    key: 'widgetPositionSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.widget_position;
    },
});
export const widgetPositionState = selector({
    key: 'widgetPositionState',
    get: ({ get }) => {
        const position = get(widgetPositionSelector);
        return {
            isEmbed: position === 'embed',
            isLivechat: position === 'livechat',
            isPopup: position === 'popup',
        };
    },
});
export const widgetAlignmentSelector = selector({
    key: 'widgetAlignmentSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.widget_alignment;
    },
});
export const avatarSelector = selector({
    key: 'avatarSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.avatar || getConfig().BRAIN_AVATAR;
    },
});
export const bubbleImageSelector = selector({
    key: 'bubbleImageSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.bubble_image;
    },
});
export const hasBubbleImageSelector = selector({
    key: 'hasBubbleImageSelector',
    get: ({ get }) => {
        const bubbleImage = get(bubbleImageSelector);
        return Boolean(bubbleImage);
    },
});
export const themeSelector = selector({
    key: 'themeSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.theme;
    },
});
export const reverseHeaderButtons = selector({
    key: 'reverseHeaderButtons',
    get: ({ get }) => {
        const config = get(userConfigState);
        const isMobile = get(isMobileSelector);
        return config.reverse_header_buttons && isMobile;
    },
});
export const launcherSelector = selector({
    key: 'launcherSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.launcher || {};
    },
});
export const isCustomBackgroundSelector = selector({
    key: 'isCustomBackgroundSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return (config.background_color.toLowerCase() !==
            DEFAULT_BACKGROUND_COLOR.toLowerCase());
    },
});
export const isCustomAccentSelector = selector({
    key: 'isCustomAccentSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return (config.accent_color.toLowerCase() !==
            DEFAULT_ACCENT_COLOR.toLowerCase() &&
            config.accent_color !== DEFAULT_ACCENT_COLOR.slice(0, 4).toLowerCase());
    },
});
export const visitorInformationSelector = selector({
    key: 'visitorInformationSelector',
    get: ({ get }) => {
        var _a;
        const config = get(userConfigState);
        return (_a = config.visitor_information) !== null && _a !== void 0 ? _a : {};
    },
});
export const focusTrapSelector = selector({
    key: 'focusTrapSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.focus_trap;
    },
});
export const autoOpenSelector = selector({
    key: 'autoOpenSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.auto_open;
    },
});
export const welcomeScreenSelector = selector({
    key: 'welcomeScreenSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.welcome_screen;
    },
});
export const chatWindowSizeSelector = selector({
    key: 'chatWindowSizeSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.chatWindow;
    },
});
/**
 * @deprecated This will be replaced isTestSelector
 */
export const showResetSelector = selector({
    key: 'showResetSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.show_reset;
    },
});
export const isTestSelector = selector({
    key: 'isTestSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.is_test;
    },
});
export const showSurveySelector = selector({
    key: 'showSurveySelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.show_survey;
    },
});
export const isMfaSelector = selector({
    key: 'isMfaSelector',
    get: ({ get }) => {
        const userConfig = get(userConfigState);
        const url = userConfig.clientUrl
            ? new URL(userConfig.clientUrl).origin
            : '';
        return !!url.match(/mfa.gr/);
    },
});
export const languageSelector = selector({
    key: 'languageSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        if ((config === null || config === void 0 ? void 0 : config.language) && config.language in languageByCountryCode) {
            return config.language;
        }
        return i18next === null || i18next === void 0 ? void 0 : i18next.language;
    },
});
export const temporalToastSelector = atom({
    key: 'temporalToastSelector',
    default: [],
});
// add a new toast with a newly generated unique id to the list of toasts
export const addToastSelector = selectorFamily({
    key: 'addToastSelector',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    get: () => () => { },
    set: (toast) => ({ get, set }) => {
        const toasts = get(temporalToastSelector);
        const newToast = Object.assign(Object.assign({}, toast), { id: uuidv4() });
        set(temporalToastSelector, [...toasts, newToast]);
    },
});
export const removeToastSelector = selectorFamily({
    key: 'removeToastSelector',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    get: () => () => { },
    set: (id) => ({ get, set }) => {
        const toasts = get(temporalToastSelector);
        const newToasts = toasts.filter((toast) => toast.id !== id);
        set(temporalToastSelector, newToasts);
    },
});
export const maxReferencesSelector = selector({
    key: 'maxReferencesSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.external_urls_limit;
    },
});
export const canUploadFileSelector = selector({
    key: 'canUploadFileSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.file_upload;
    },
});
export const showManualLinkHandlingSelector = selector({
    key: 'showManualLinkHandlingSelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.manual_link_handling;
    },
});
export const fontFamilySelector = selector({
    key: 'fontFamilySelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.font_family;
    },
});
export const closeSessionIfEmptySelector = selector({
    key: 'closeSessionIfEmptySelector',
    get: ({ get }) => {
        const config = get(userConfigState);
        return config.close_session_if_empty;
    },
});
